/**
 * Display helpers for the Paid Media Overview v2 page. All purely client-side
 * — the backend already humanises strings (annotations, headlines), so we
 * only format raw numbers + booleans here.
 */

import type {
  OverviewV2Direction,
  OverviewV2KpiKey,
  OverviewV2MoverMetric,
} from "@/schema/brand-os/paid-media-overview-v2";

const CURRENCY_SYMBOL: Record<string, string> = {
  INR: "₹",
  USD: "$",
  EUR: "€",
  GBP: "£",
};

function symbolFor(currency: string) {
  return CURRENCY_SYMBOL[currency.toUpperCase()] ?? `${currency} `;
}

/** Indian-friendly compact spend formatter (₹1.2L / ₹15K / ₹950). */
export function formatCurrencyCompact(value: number | null | undefined, currency: string): string {
  if (value == null) return "—";
  const sym = symbolFor(currency);
  const abs = Math.abs(value);
  if (abs >= 10_000_000) return `${sym}${(value / 10_000_000).toFixed(1)}Cr`;
  if (abs >= 100_000) return `${sym}${(value / 100_000).toFixed(1)}L`;
  if (abs >= 1_000) return `${sym}${(value / 1_000).toFixed(1)}K`;
  return `${sym}${value.toLocaleString("en-IN")}`;
}

/** Plain currency for CPA-ish numbers where compact suffixes obscure precision. */
export function formatCurrencyPrecise(value: number | null | undefined, currency: string): string {
  if (value == null) return "—";
  const sym = symbolFor(currency);
  return `${sym}${value.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

/**
 * Per-KPI value formatter. Backend may emit `null` → render an em-dash so
 * the user sees "no data" rather than "0" (which is a meaningful value
 * for spend / conversions).
 */
export function formatKpiValue(
  key: OverviewV2KpiKey,
  value: number | null,
  currency: string,
): string {
  if (value == null) return "—";
  switch (key) {
    case "roas":
      return `${value.toFixed(2)}x`;
    case "cpa":
      return formatCurrencyPrecise(value, currency);
    case "spend":
      return formatCurrencyCompact(value, currency);
    case "conversions":
      return value.toLocaleString("en-IN");
  }
}

/**
 * Display label for a mover metric. Default branch handles the "permissive
 * schema" case — if the backend grows a new metric (e.g. `aov`), the page
 * keeps rendering with the raw key uppercased instead of crashing.
 * `noImplicitReturns` also requires a fallthrough.
 */
export function formatMoverMetric(metric: OverviewV2MoverMetric): string {
  switch (metric) {
    case "roas": return "ROAS";
    case "cpa": return "CPA";
    case "spend": return "Spend";
    case "ctr": return "CTR";
    case "cpc": return "CPC";
    case "conv_rate": return "Conv. rate";
    default: return String(metric).toUpperCase();
  }
}

/**
 * Signed percentage formatter. `delta_pct: null` is the snapshot's "insufficient
 * baseline" signal — render an em-dash, NOT "0%" (per backend rendering tips).
 */
export function formatDelta(deltaPct: number | null): string {
  if (deltaPct == null) return "—";
  const sign = deltaPct > 0 ? "+" : "";
  return `${sign}${deltaPct.toFixed(1)}%`;
}

/**
 * Colour for a delta. Sign-driven by default: positive % → green,
 * negative % → red, zero / null → neutral.
 *
 * For "lower-is-better" metrics (CPA), we invert: an increase is bad
 * (red), a decrease is good (green). Pass the KPI key via `kpiKey` to
 * opt into this behaviour.
 *
 * `direction` is the fallback for the rare cases where a numeric delta
 * isn't available (e.g. sparkline trend tone derived from first-vs-last
 * points). When `deltaPct` is provided it always wins.
 */
const LOWER_IS_BETTER: ReadonlySet<OverviewV2KpiKey> = new Set(["cpa"]);

export function deltaTone(
  deltaPct: number | null | undefined,
  direction?: OverviewV2Direction | "up" | "down",
  kpiKey?: OverviewV2KpiKey,
): "positive" | "negative" | "neutral" {
  const invert = kpiKey ? LOWER_IS_BETTER.has(kpiKey) : false;
  if (deltaPct != null) {
    if (deltaPct > 0) return invert ? "negative" : "positive";
    if (deltaPct < 0) return invert ? "positive" : "negative";
    return "neutral";
  }
  if (!direction || direction === "stable") return "neutral";
  const up = direction === "up";
  return up === !invert ? "positive" : "negative";
}

/**
 * Tab/badge label for a platform group. The synthetic backend value
 * `"combined"` is rendered as "All" because that's how the rolled-up view
 * is named in the UI (the Σ glyph next to it is the hint that it's
 * derived, not a separate data source). Unknown platforms (e.g. a future
 * "tiktok") fall through with a Title-Case rendering of the raw string.
 */
export function platformLabel(platform: string): string {
  if (platform === "google") return "Google";
  if (platform === "meta") return "Meta";
  if (platform === "combined") return "All";
  return platform.charAt(0).toUpperCase() + platform.slice(1);
}

/**
 * "active now" / "in 3 days" — `lead_time_days: 0` is the curated provider's
 * way of saying the signal is currently in effect.
 */
export function formatLeadTime(days: number): string {
  if (days <= 0) return "Now";
  if (days === 1) return "in 1 day";
  return `in ${days} days`;
}

/**
 * Relative "last updated" — "just now", "3m ago", "2h ago", or a date.
 * The backend's `synced_at` is always UTC ISO; new Date() handles parse.
 */
export function formatRelativeUpdated(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffMs = Date.now() - then;
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return new Date(iso).toLocaleDateString("en-IN", { month: "short", day: "numeric" });
}
